import { VuexModule, Module, Action, Mutation } from 'vuex-module-decorators'
import axios from 'axios'
import { escapeJson } from '@/utils/string'
import store from '@/store'
import { IRawTranslation, IRoot } from '@/data/json/json'
import { ITranslation } from '@/data/translation'
import Optician from '@/data/optician'
import Glass from '@/data/glass'
import GlassType from '@/data/glass-type'
import Frame from '@/data/frame'
import GlassOption, { getInformationCode, getOptionIcon } from '@/data/glass-option'
import GlassOptionValue from '@/data/glass-option-value'
import InformationWebsite from '@/data/information-website'
import InformationWebsiteTab from '@/data/information-website-tab'
import { InformationCodes, BasketType } from '@/data/enums'
import GlassColor from '@/data/glasscolor'

export interface IDataState {
  colors: GlassColor[]
  data?: IRoot
  glasses: Glass[]
  hasData: boolean
  optician: Optician
  popups: InformationWebsite[]
  popupTabs: InformationWebsiteTab[]
}

@Module({ dynamic: true, store, name: 'data', namespaced: true })
class Data extends VuexModule implements IDataState {
  public colors: GlassColor[] = []
  public data?: IRoot = undefined
  public dneye: boolean = false
  public glasses: Glass[] = []
  public hasData: boolean = false
  public optician: Optician = new Optician({})
  public popups: InformationWebsite[] = []
  public popupTabs: InformationWebsiteTab[] = []
  public translations: object = {}

  // ACTIONS
  @Action({ rawError: true })
  public async setJson (json: string): Promise<void> {
    json = escapeJson(json)
    const allData = JSON.parse(json)
    const optician = new Optician({
      id: allData.opticien[0].id,
      logo: allData.opticien[0].logo,
      name: allData.opticien[0].naam,
      priceType: allData.opticien[0].prijzen,
      basketType: allData.opticien[0].winkelmand,
      discountPercentage: allData.opticien[0].actie_percentage,
      discountTitle: allData.opticien[0].actie_titel,
      initialGender: allData.opticien[0].geslacht,
      basketTitle: allData.opticien[0].basket_titel,
      glassImageType: 0
    })

    // image set
    if (allData.opticien[0].glass_imageset) {
      optician.glassImageType = allData.opticien[0].glass_imageset
    }

    // nlook twice campaign
    if (allData.opticien[0].looktwice && allData.opticien[0].looktwice === 1) {
      optician.looktwice = true
    }

    optician.altTitleSun = allData.opticien[0].titel_sun
    optician.altTitleScreen = allData.opticien[0].titel_bsb

    optician.frameSelectionTitle = allData.opticien[0].titel_popup
    optician.frameSelectionSubTitle = allData.opticien[0].subtitel_popup
    optician.frameSelectionButton = allData.opticien[0].call_to_action

    optician.frames = []
    for (let fr = 0; fr < 8; fr++) {
      const frame = new Frame()
      optician.frames.push(frame)
    }

    // add frames
    optician.frames[0].price = allData.opticien[0].prijs1
    optician.frames[0].id = 'r1425b'

    optician.frames[1].price = allData.opticien[0].prijs2
    optician.frames[1].id = 'r1426a'

    optician.frames[2].price = allData.opticien[0].prijs3
    optician.frames[2].id = 'r1429b'

    optician.frames[3].price = allData.opticien[0].prijs4
    optician.frames[3].id = 'r3309b'

    optician.frames[4].price = allData.opticien[0].prijs5
    optician.frames[4].id = 'r3311a'

    optician.frames[5].price = allData.opticien[0].prijs6
    optician.frames[5].id = 'r3312a'

    optician.frames[6].price = allData.opticien[0].prijs7
    optician.frames[6].id = 'r3316b'

    optician.frames[7].price = allData.opticien[0].prijs8
    optician.frames[7].id = 'r3317b'

    // dneye
    optician.dneye = false
    if (allData.opticien[0].dneye && allData.opticien[0].dneye !== 0) {
      optician.dneye = true
    }

    // impressionist
    optician.impressionist = false
    if (allData.opticien[0].impressionist && allData.opticien[0].impressionist !== 0) {
      optician.impressionist = true
    }

    optician.showPrices = true
    if (allData.opticien[0].prijzen === 0) {
      optician.priceTypeText = ''
      optician.showPrices = false
    } else if (allData.opticien[0].prijzen === 1) {
      optician.priceTypeText = 'glass_price_piece'
    } else {
      optician.priceTypeText = 'glass_price_pair'
    }

    this.SET_DATA(allData)
    this.SET_DNEYE(allData.opticien[0].dneye)
    this.SET_OPTICIAN(optician)
    this.PROCESS_DATA()
    await this.context.dispatch('setPopups', allData)
    await this.context.dispatch('setTheme', allData.opticien[0].folder)
    await this.context.dispatch('setTranslations', allData)
  }

  @Action({ commit: 'SET_POPUPS', rawError: true })
  public async setPopups (allData: IRoot): Promise<InformationWebsite[]> {
    return allData.popup.map((popup) => {
      const informationWebsite = new InformationWebsite(popup)
      informationWebsite.tabs = allData.popup_tab
        .filter((tab) => tab.popup_taal_id === popup.id)
        .map((popupTab) => (new InformationWebsiteTab(popupTab)))
      informationWebsite.tabs.sort((a, b) => {
        return a.sortOrder - b.sortOrder
      })
      return informationWebsite
    })
  }

  @Action({ rawError: true })
  public async setTheme (name: string): Promise<any> {
    let theme = 'theme/default/'
    if (name && name.length) {
      try {
        await axios.get(
          `${process.env.BASE_URL}theme/${name}`,
          {
            validateStatus (status) {
              return status !== 404
            }
          }
        )
        theme = `theme/${name}/`
      } catch (err) {
        // Do nothing
      }
    }
    await this.context.dispatch('setTheme', theme, { root: true })
  }

  @Action({ commit: 'SET_TRANSLATIONS', rawError: true })
  public async setTranslations (allData: IRoot): Promise<ITranslation[]> {
    const translations: ITranslation[] = []
    allData.vertaling
      .filter((rawTranslation: IRawTranslation) => (rawTranslation.code_int))
      .forEach((rawTranslation: IRawTranslation) => {
        let text = String(rawTranslation.begrip)
        if (rawTranslation.html) {
          text = String(rawTranslation.html)
        }
        translations.push({
          code: String(rawTranslation.code_int),
          text
        })
      })
    return translations
  }

  @Mutation
  public PROCESS_DATA () {
    this.hasData = true
    this.glasses = []

    if (this.data !== undefined && this.data.hasOwnProperty('glas')) {
      // TEMP ARRAYS
      const tempGlasses: Glass[] = []
      const tempGlassTypes: GlassType[] = []
      const tempGlassOptions: GlassOption[] = []
      const tempGlassOptionValues: GlassOptionValue[] = []
      const tempGlassOptionValueColors: GlassColor[] = []

      // CREATE GLASSES
      this.data.glas.forEach((glassItem, index) => {
        const sunglasses = glassItem.sun === 1
        const g = new Glass({
          id: glassItem.id,
          actionPercentage: glassItem.actie_percentage,
          actionTitle: glassItem.actie_titel,
          actionSubTitle: glassItem.actie_subtitel,
          name: glassItem.naam,
          sunglasses,
          usps: [
            glassItem.usp1,
            glassItem.usp2,
            glassItem.usp3,
            glassItem.usp4,
            glassItem.usp5,
            glassItem.usp6,
            glassItem.usp7
          ]
        })

        if (index === 0) {
          g.icon = 'glasstype-single'
          g.informationCode = InformationCodes.GlassTypeSingle
          g.isSingle = true
        }
        if (index === 1) {
          g.icon = 'glasstype-multifocal'
          g.informationCode = InformationCodes.GlassTypeMulti
          g.isMulti = true
        }
        if (index === 2) {
          g.icon = 'glasstype-screen'
          g.informationCode = InformationCodes.GlassTypeScreen
          g.screenGlasses = true
        }
        if (index === 3) {
          g.icon = 'glasstype-singlesunglasses'
          g.informationCode = InformationCodes.GlassTypeSingle
          g.isSingle = true
          g.sunglasses = true
        }
        if (index === 4) {
          g.icon = 'glasstype-multifocalsunglasses'
          g.informationCode = InformationCodes.GlassTypeMulti
          g.isMulti = true
          g.sunglasses = true
        }

        tempGlasses.push(g)
      })

      if (tempGlasses.length < 5) {
        const g = new Glass({
          id: '4',
          available: false,
          isMulti: true,
          sunglasses: true,
          icon: 'glasstype-multifocalsunglasses',
          name: 'glass_multifocal_sun'
        })
        tempGlasses.push(g)
      }

      // CREATE TYPES
      this.data.glastype.forEach((glassTypeItem) => {
        const t = new GlassType()
        const glassTypeData = {
          id: glassTypeItem.id,
          glassId: glassTypeItem.glas_id,
          name: glassTypeItem.naam,
          price: parseFloat(String(glassTypeItem.basisprijs)),
          description: glassTypeItem.subtitel,
          subTitle: glassTypeItem.subtitel2,
          priceTypeText: this.optician.priceTypeText,
          priceType: this.optician.priceType,
          uspValues: [
            glassTypeItem.usp1_waarde,
            glassTypeItem.usp2_waarde,
            glassTypeItem.usp3_waarde,
            glassTypeItem.usp4_waarde,
            glassTypeItem.usp5_waarde,
            glassTypeItem.usp6_waarde
          ],
          discountPrice: glassTypeItem.actie_prijs,
          discountScreenPrice: glassTypeItem.actie_prijs_bsb,
          discountTitle: glassTypeItem.actie_prefix,
          discountPercentage: this.optician.discountPercentage,
          promotionTitle: glassTypeItem.actie_titel
        }

        if (glassTypeItem.standaard === 1) {
          t.isDefault = true
        }
        if (glassTypeItem.dneye === 1) {
          t.hasDneye = true
        }
        if (glassTypeItem.impressionist === 1) {
          t.hasImpressionist = true
        }

        Object.assign(t, { ...glassTypeData })
        tempGlassTypes.push(t)
      })

      // CREATE OPTIONS
      this.data.glasoptie.forEach((glassOptionItem) => {
        const o = new GlassOption()
        const glassOptionData = {
          id: glassOptionItem.id,
          fullscreen: glassOptionItem.fullscreen,
          icon: getOptionIcon(glassOptionItem.nummer),
          informationCode: getInformationCode(glassOptionItem.nummer),
          name: glassOptionItem.naam,
          optionId: glassOptionItem.nummer,
          typeId: glassOptionItem.glas_type
        }
        Object.assign(o, { ...glassOptionData })
        tempGlassOptions.push(o)
      })

      // CREATE OPTION VALUES
      this.data.glaswaarde.forEach((glassOptionValueItem) => {
        if (glassOptionValueItem.naam === 'DNEye') {
          console.log(glassOptionValueItem)
        }
        const v = new GlassOptionValue()
        let isDefaultSelected = false
        if (glassOptionValueItem.default && glassOptionValueItem.default === 1) {
          isDefaultSelected = true
        }
        const glassOptionValueData = {
          id: glassOptionValueItem.id,
          optionId: glassOptionValueItem.glas_optie,
          name: glassOptionValueItem.naam,
          number: glassOptionValueItem.nummer,
          price: glassOptionValueItem.meerprijs || 0,
          hasExtra: glassOptionValueItem.gebruikt_extra || 0,
          isDefault: glassOptionValueItem.default === 1,
          isExtra: glassOptionValueItem.is_extra || 0,
          selected: isDefaultSelected,
          subtitle: glassOptionValueItem.subtitel,
          colorSetId: glassOptionValueItem.kleurset_id
        }
        Object.assign(v, { ...glassOptionValueData })
        tempGlassOptionValues.push(v)
      })

      // CREATE COLORS
      this.data.kleurset_kleuren_all.forEach((color) => {
        const clr = new GlassColor()
        const glassColorData = {
          id: color.id,
          colorSetId: color.kleurset_id,
          name: color.naam,
          order: color.volgorde,
          image: color.afbeelding_bestand
        }
        Object.assign(clr, { ...glassColorData })
        tempGlassOptionValueColors.push(clr)
      })

      tempGlassTypes.forEach((glassType) => {
        const glassesIndex = tempGlasses.findIndex((glasses) => glasses.id === glassType.glassId)
        if (glassesIndex !== -1) {
          let glassOptions = tempGlassOptions.filter((glassOption) => glassOption.typeId === glassType.id)
          glassOptions = glassOptions.map((option) => {
            if (option.optionId === 5) { // dneye
              glassType.hasDneye = true
            }

            option.values = tempGlassOptionValues.filter((optionValue) => optionValue.optionId === option.id)
            return option
          })
          glassType.options = glassOptions
          glassType.usps = tempGlasses[glassesIndex].usps.map((usp, index) => {
            return { title: usp, value: glassType.uspValues[index] }
          })
          tempGlasses[glassesIndex].glassTypes.push(glassType)
        }
      })

      this.colors = tempGlassOptionValueColors
      this.glasses = tempGlasses
    }
  }

  @Mutation
  private SET_DATA (opticianData: IRoot) {
    this.data = opticianData
  }

  @Mutation
  private SET_DNEYE (dneye: number) {
    this.dneye = (dneye !== 0)
  }

  @Mutation
  private SET_OPTICIAN (opticianData: Optician) {
    this.optician = new Optician(opticianData)
  }

  @Mutation
  private SET_POPUPS (popups: InformationWebsite[]) {
    this.popups = popups
  }

  @Mutation
  private SET_TRANSLATIONS (translations: ITranslation[]) {
    translations.forEach((translation) => {
      this.translations = { ...this.translations, [translation.code]: translation.text }
    })
  }
}

export default Data
